import React, { forwardRef, ReactNode } from 'react';
import { FloatingFocusManager, FloatingNode, FloatingPortal } from '@floating-ui/react-dom-interactions';
import { css, cx } from '@linaria/atomic';
import { AnimatePresence, motion } from 'motion/react';
import { useThemeValues } from '../../../../hooks';
import { CalendarDialogCalendar } from './calendar-dialog-calendar.component';
import type { UseDataPickerDialogResponse } from './use-date-picker';

const componentMotionDialogStyle1 = css`
  border: none;
  overflow: auto;
  background: white;
  padding-top: 0;
  &:focus {
    outline: none;
  }
`;

type Props = UseDataPickerDialogResponse['dialogProps'] & {
  children: ReactNode;
  onFocus?: (event: React.FocusEvent<HTMLDialogElement>) => void;
};

type DragEventKeys =
  | 'onDrag'
  | 'onDragEnd'
  | 'onDragEnter'
  | 'onDragExit'
  | 'onDragLeave'
  | 'onDragOver'
  | 'onDragStart'
  | 'onAnimationStart';
type DialogProps = Omit<React.ComponentPropsWithoutRef<'dialog'>, DragEventKeys> & Props;

export const DatePickerDialogPre = forwardRef<HTMLDialogElement, DialogProps>(
  ({ open, nodeId, context, children, labelId, descriptionId, initialFocus, style: floatingStyle, ...props }, ref) => {
    const theme = useThemeValues();

    return (
      <FloatingNode id={nodeId}>
        <FloatingPortal>
          <AnimatePresence initial={false}>
            {open && (
              <FloatingFocusManager context={context} returnFocus={false} initialFocus={initialFocus}>
                <motion.dialog
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  ref={ref}
                  open={open}
                  aria-describedby={descriptionId}
                  aria-labelledby={labelId}
                  {...props}
                  css={{
                    ...floatingStyle,
                    boxShadow: theme.shadows.heavy,
                    borderRadius: context.placement.includes('bottom')
                      ? theme.spacing(0, 0, 1, 1)
                      : theme.spacing(1, 1, 0, 0),
                    zIndex: theme.zIndex.popover,
                  }}
                  className={cx(componentMotionDialogStyle1, props.className)}
                >
                  {children}
                </motion.dialog>
              </FloatingFocusManager>
            )}
          </AnimatePresence>
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

const DatePickerDialogNamespace = Object.assign(DatePickerDialogPre, {
  Calendar: CalendarDialogCalendar,
});

export { DatePickerDialogNamespace as DatePickerDialog };
